import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, FileCheck2, Send, ThumbsUp, ThumbsDown, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { InvoiceDocument } from "@/components/InvoiceDocument";
import { Button } from "@/components/ui/button";
import { PdfButton } from "@/components/PdfButton";
import { QuoteBadge } from "./Quotes";

export default function QuoteView() {
  const { id } = useParams();
  const { t } = useLang();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [co, setCo] = useState(null);
  useEffect(() => {
    api.get(`/quotes/${id}`).then((r) => setQ(r.data));
    api.get("/company").then((r) => setCo(r.data));
  }, [id]);
  if (!q || !co) return <div className="text-slate-400">{t("loading")}</div>;
  const setStatus = async (status) => {
    try { setQ((await api.patch(`/quotes/${id}/status`, { status })).data); toast.success(t("saved")); } catch (e) { toast.error(errMsg(e)); }
  };
  const convert = async () => {
    try {
      const { data } = await api.post(`/quotes/${id}/convert`);
      toast.success(t("quoteConverted"));
      nav(`/invoices/${data.id}`);
    } catch (e) { toast.error(errMsg(e) === "PLAN_INVOICES" ? t("planInvoicesLocked") : errMsg(e)); }
  };
  const send = async () => {
    try {
      setQ((await api.post(`/quotes/${id}/send`)).data);
      toast.success(t("quoteSent"));
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast.error(code === "client_no_email" ? t("clientNoEmail") : code === "email_failed" ? t("emailFailed") : errMsg(e));
    }
  };
  const done = q.status === "converted";
  return (
    <div data-testid="quote-view-page">
      <div className="no-print mb-6 flex flex-wrap items-center gap-2">
        <Button asChild variant="ghost" data-testid="quote-back-btn"><Link to="/quotes"><ArrowLeft className="me-1 h-4 w-4 rtl:-scale-x-100" />{t("back")}</Link></Button>
        <QuoteBadge status={q.status} />
        {done && q.invoice_id && <Link data-testid="quote-invoice-link" to={`/invoices/${q.invoice_id}`} className="text-sm font-semibold text-[#007A3D] hover:underline">{t("viewInvoice")} →</Link>}
        <div className="ms-auto flex flex-wrap gap-2">
          {!done && <Button variant="outline" data-testid="quote-send-btn" onClick={send}><Mail className="me-1 h-4 w-4" />{t("sendQuoteEmail")}</Button>}
          {!done && q.status === "draft" && <Button variant="outline" data-testid="quote-mark-sent-btn" onClick={() => setStatus("sent")}><Send className="me-1 h-4 w-4" />{t("q_markSent")}</Button>}
          {!done && q.status !== "accepted" && <Button variant="outline" data-testid="quote-accept-btn" onClick={() => setStatus("accepted")}><ThumbsUp className="me-1 h-4 w-4" />{t("q_accept")}</Button>}
          {!done && q.status !== "declined" && <Button variant="outline" data-testid="quote-decline-btn" onClick={() => setStatus("declined")}><ThumbsDown className="me-1 h-4 w-4" />{t("q_decline")}</Button>}
          {!done && <Button data-testid="quote-convert-btn" onClick={convert} className="bg-[#007A3D] hover:bg-[#005E2E]"><FileCheck2 className="me-1 h-4 w-4" />{t("convertToInvoice")}</Button>}
          <PdfButton kind="quote" id={id} name={q.number} testid="quote-pdf-btn" />
          <Button data-testid="quote-print-btn" onClick={() => window.print()} className="bg-[#0B1220] hover:bg-slate-800"><Printer className="me-1 h-4 w-4" />{t("print")}</Button>
        </div>
      </div>
      <InvoiceDocument inv={q} co={co} kind="quote" />
    </div>
  );
}
